// A PO's Costs tab: landed costs (freight, duty, tariffs, fees from a forwarder's invoice) on this PO or on any of
// its receipts. Everything is worked out here from synced rows by shared/landed-cost.ts as you type: each charge in
// USD, its split by value, weight or quantity, and each line's landed unit cost. Apply puts local items' shares on
// the stock ledger (cost adjustments); Shopify items can have their unit cost updated to a weighted average, after
// a preview, through the catalog editor (shopify.edit_variants). Documents (Mitch, 2026-11-03: "Costs on a PO need to
// have a drag/drop for a document PDF or otherwise"): each landed cost keeps its files (the forwarder's invoice; the
// attachment owner landed_cost), dropped, pasted or chosen on its box; a file dropped anywhere on the tab goes on the
// landed cost open, or starts one.
import { AlertTriangle, Calculator, Plus, Store, Trash2 } from "lucide-react";
import { type DragEvent, useMemo, useState } from "react";
import { numberOf } from "../../../../shared/calc.ts";
import type { LandedCost, Po } from "../../../../shared/db.gen.ts";
import {
  ALLOCATION_LABELS,
  ALLOCATION_METHODS,
  type AllocationMethod,
  type AppliedCost,
  allocate,
  basisLines,
  CHARGE_KIND_LABELS,
  CHARGE_KINDS,
  type ChargeInput,
  chargeUsd,
  type LineResult,
  type Rates,
  receiptsInScope,
  shopifyCostPreview,
} from "../../../../shared/landed-cost.ts";
import { costedPoLines } from "../../../../shared/po.ts"; // An accepted invoice price is the cost ---
import { ago, money, qty } from "../../lib/format.ts";
import { can, mutate, mutateWithResult } from "../../lib/mutate.ts";
import { me } from "../../lib/session.ts";
import { useRows } from "../../lib/sync.ts";
import { Attachments, uploadAttachment } from "../../ui/Attachments.tsx";
import { Badge, Button, cx, Input, Select } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { DialogLayer } from "../../ui/DialogLayer.tsx";
import { toast } from "../../ui/toast.tsx";

const STATUS_TONE = { draft: "neutral", applied: "ok", void: "danger" } as const;
const usd = (n: number | null | undefined) => (n == null ? "—" : money(n));
/** A charge's amount as typed, in its own currency (any code can be typed, so no symbol). */
const amountText = (n: number) => Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const kindLabel = (kind: string) => CHARGE_KIND_LABELS[kind as keyof typeof CHARGE_KIND_LABELS] ?? kind;

type ChargeRow = ChargeInput & { landed_cost_id: string };
type LineRow = LineResult & { id: string };
const num = (text: string) => {
  const n = numberOf(text);
  return text.trim() !== "" && Number.isFinite(n) ? n : null;
};

/**
 * A non-USD charge still at a USD rate of 1 is almost always a rate nobody entered: ask first. undefined = nothing to
 * ask, true = confirmed, false = go back and fix it.
 */
function confirmRates(charges: { currency: string; usd_rate: number }[]): boolean | undefined {
  const suspect = [...new Set(charges.filter((c) => c.currency.toUpperCase() !== "USD" && Number(c.usd_rate) === 1).map((c) => c.currency))];
  if (!suspect.length) return undefined;
  return confirm(`The ${suspect.join(", ")} charge has a USD rate of 1. Is 1 really right? Cancel to enter the rate from the invoice.`);
}

/** An input that saves when it loses focus (Enter too), like the side panels' fields. */
function SaveInput({
  value,
  onSave,
  className,
  disabled,
  ...rest
}: {
  value: string;
  onSave: (v: string) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  "aria-label"?: string;
  type?: string;
}) {
  return (
    <Input
      key={value}
      defaultValue={value}
      disabled={disabled}
      className={cx("h-7 text-[12px]", className)}
      onBlur={(e) => e.target.value !== value && onSave(e.target.value)}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      {...rest}
    />
  );
}

/** The landed costs touching a PO, with this PO's receipts for picking. */
function usePoLandedCosts(po: Po) {
  const all = useRows("landed_cost");
  const links = useRows("landed_cost_receipt");
  const receipts = useRows("receipt");
  return useMemo(() => {
    const mine = new Set(receipts.filter((r) => r.po_id === po.id).map((r) => r.id));
    const touching = new Set(links.filter((l) => mine.has(l.receipt_id)).map((l) => l.landed_cost_id));
    return all.filter((lc) => (lc.scope === "po" && lc.po_id === po.id) || touching.has(lc.id)).sort((a, b) => b.created_at.localeCompare(a.created_at));
  }, [all, links, receipts, po.id]);
}

export function PoLandedCost({ po }: { po: Po }) {
  const list = usePoLandedCosts(po);
  const [openId, setOpenId] = useState<string | null>(null);
  const open = list.find((lc) => lc.id === openId) ?? list[0];

  const create = async (): Promise<string | null> => {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    setOpenId(id);
    const { ok } = await mutateWithResult("landed_cost.create", { id, po_id: po.id }, [
      {
        t: "landed_cost",
        id,
        row: {
          id,
          number: "LC-…",
          scope: "po",
          po_id: po.id,
          status: "draft",
          forwarder: "",
          invoice_ref: "",
          invoice_date: null,
          goods_rates: {},
          notes: "",
          applied_at: null,
          applied_by: null,
          shopify_costs: [],
          created_by: me()?.id ?? null,
          created_at: now,
          updated_at: now,
        },
      },
    ]);
    return ok ? id : null;
  };

  // A document dropped anywhere on the tab: onto the landed cost open (a draft or not), else a new one with it.
  const [over, setOver] = useState(false);
  const canDrop = can("attachment.update") && (!!open || can("landed_cost.create"));
  const files = (e: DragEvent) => [...e.dataTransfer.types].includes("Files");
  const onDrop = async (e: DragEvent) => {
    setOver(false);
    if (!canDrop || !e.dataTransfer.files.length) return;
    e.preventDefault();
    const dropped = [...e.dataTransfer.files];
    const id = open?.id ?? (await create());
    if (!id) return;
    for (const f of dropped) uploadAttachment({ type: "landed_cost", id }, f, { kind: "invoice" }).catch((err: Error) => toast.error(err.message));
  };

  return (
    <div
      className={cx("relative min-h-0 flex-1 overflow-y-auto", over && "outline-2 -outline-offset-2 outline-accent outline-dashed")}
      onDragOver={(e) => {
        if (!canDrop || !files(e)) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
      }}
      onDrop={onDrop}
    >
      {over && (
        <div className="pointer-events-none absolute inset-x-0 top-12 z-10 mx-auto w-fit rounded-md bg-accent px-3 py-1.5 text-[12px] font-medium text-white shadow-pop">
          {open ? `Drop to attach to ${open.number}` : "Drop to start a landed cost with it"}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2">
        <Calculator className="size-4 text-muted" />
        <span className="font-medium">Landed cost</span>
        {list.map((lc) => (
          <button
            key={lc.id}
            type="button"
            onClick={() => setOpenId(lc.id)}
            className={cx("rounded px-2 py-0.5 font-mono text-[12px]", lc.id === open?.id ? "bg-accent-soft text-accent-text" : "text-muted hover:bg-hover")}
          >
            {lc.number}
            {lc.status !== "draft" ? ` · ${lc.status}` : ""}
          </button>
        ))}
        {can("landed_cost.create") && (
          <Button size="sm" variant={list.length ? "ghost" : "primary"} icon={<Plus className="size-3.5" />} className="ml-auto" onClick={create}>
            Add freight, duty or fees
          </Button>
        )}
      </div>
      {open ? (
        <LandedCostEditor key={open.id} lc={open} po={po} />
      ) : (
        <div className="px-4 py-6 text-muted">
          When the forwarder's invoice arrives, add its freight, duty, tariffs and fees here (or drop the invoice's PDF here to start). They're split over what
          was received (by value, weight or quantity) to give each line's landed unit cost, which can go on the stock ledger and update Shopify's unit cost.
        </div>
      )}
    </div>
  );
}

function LandedCostEditor({ lc, po }: { lc: LandedCost; po: Po }) {
  const links = useRows("landed_cost_receipt");
  const allCharges = useRows("landed_cost_charge");
  const receipts = useRows("receipt");
  const receiptLines = useRows("receipt_line");
  const poLines = costedPoLines(useRows("po_line")); // An accepted invoice price is the cost ---
  const pos = useRows("po");
  const items = useRows("item");
  const adjustments = useRows("stock_cost_adjustment");
  const [previewing, setPreviewing] = useState(false);

  const chosen = useMemo(() => links.filter((l) => l.landed_cost_id === lc.id), [links, lc.id]);
  const charges = useMemo(
    () =>
      allCharges
        .filter((c) => c.landed_cost_id === lc.id)
        .sort((a, b) => a.position - b.position || a.created_at.localeCompare(b.created_at)) as unknown as (ChargeInput & { landed_cost_id: string })[],
    [allCharges, lc.id],
  );
  const rates = (lc.goods_rates ?? {}) as Rates;
  const inScope = useMemo(
    () =>
      receiptsInScope(
        lc,
        chosen.map((l) => l.receipt_id),
        receipts,
      ),
    [lc, chosen, receipts],
  );
  const { lines, estimate } = useMemo(
    () => basisLines(lc, { receipts: inScope, receiptLines, poLines, pos, items }),
    [lc, inScope, receiptLines, poLines, pos, items],
  );
  const a = useMemo(() => allocate(lines, charges, rates), [lines, charges, rates]);
  const draft = lc.status === "draft";
  const editable = draft && can("landed_cost.update");
  const currencies = [...new Set(lines.map((l) => l.currency.toUpperCase()).filter((c) => c !== "USD"))];
  const onLedger = adjustments.filter((x) => x.landed_cost_id === lc.id).reduce((n, x) => n + Number(x.amount), 0);
  const multiPo = new Set(lines.map((l) => l.po_id)).size > 1;
  const shopifyLines = a.lines.filter((l) => l.shopify);

  const update = (patch: Record<string, unknown>) => mutate("landed_cost.update", { id: lc.id, patch });
  const addCharge = () =>
    mutate("landed_cost_charge.create", {
      id: crypto.randomUUID(),
      landed_cost_id: lc.id,
      position: charges.length ? Math.max(...charges.map((c) => c.position)) + 1 : 0,
      kind: charges.length ? "duty" : "freight",
      method: charges.length ? "value" : "weight",
      currency: "USD",
      usd_rate: 1,
      amount: 0,
    });
  const saveCharge = (id: string, patch: Record<string, unknown>) => mutate("landed_cost_charge.update", { id, patch });
  const act = async (name: "landed_cost.apply" | "landed_cost.void" | "landed_cost.reopen", confirmText?: string) => {
    if (confirmText && !confirm(confirmText)) return;
    let confirm_rates: boolean | undefined;
    if (name === "landed_cost.apply") {
      confirm_rates = confirmRates(charges);
      if (confirm_rates === false) return;
    }
    const { ok, result } = await mutateWithResult(name, name === "landed_cost.apply" ? { id: lc.id, confirm_rates } : { id: lc.id });
    if (ok && name === "landed_cost.apply") {
      const r = result as { ledger_usd: number; shopify_items: number };
      toast.ok(
        `${money(r.ledger_usd)} on the stock ledger${r.shopify_items ? `; ${r.shopify_items} Shopify item${r.shopify_items === 1 ? "" : "s"} can take the new cost` : ""}`,
      );
    }
  };
  const remove = async () => {
    if (confirm(`Delete draft ${lc.number}?`)) await mutate("landed_cost.remove", { id: lc.id });
  };

  // Receipts to pick from: this PO's, plus any already on it (from other POs), plus recent ones from other POs.
  const pickable = useMemo(() => {
    const on = new Set(chosen.map((l) => l.receipt_id));
    return receipts.filter((r) => r.po_id === po.id || on.has(r.id)).sort((x, y) => x.number.localeCompare(y.number, undefined, { numeric: true }));
  }, [receipts, chosen, po.id]);
  const others = useMemo(() => {
    const on = new Set(chosen.map((l) => l.receipt_id));
    return receipts
      .filter((r) => r.po_id !== po.id && !on.has(r.id))
      .sort((x, y) => y.received_at.localeCompare(x.received_at))
      .slice(0, 200);
  }, [receipts, chosen, po.id]);
  const toggleReceipt = (receiptId: string) => {
    const link = chosen.find((l) => l.receipt_id === receiptId);
    if (link) mutate("landed_cost_receipt.remove", { id: link.id });
    else mutate("landed_cost_receipt.create", { id: crypto.randomUUID(), landed_cost_id: lc.id, receipt_id: receiptId });
  };
  const setScope = async (scope: "po" | "receipts") => {
    if (scope === lc.scope) return;
    await update({ scope });
    // Picking receipts starts from this PO's.
    if (scope === "receipts" && !chosen.length)
      for (const r of receipts.filter((x) => x.po_id === po.id))
        await mutate("landed_cost_receipt.create", { id: crypto.randomUUID(), landed_cost_id: lc.id, receipt_id: r.id });
  };

  const flagOf = (id: string) => a.charges.find((x) => x.id === id)?.flag ?? "";
  const chargeColumns: Column<ChargeRow>[] = [
    {
      key: "kind",
      header: "Kind",
      width: "110px",
      value: (c) => kindLabel(c.kind),
      edit: {
        options: CHARGE_KINDS.map((k) => ({ value: k, label: CHARGE_KIND_LABELS[k] })),
        get: (c) => c.kind,
        save: (c, v) => saveCharge(c.id, { kind: v }),
      },
    },
    {
      key: "description",
      header: "Description",
      width: "minmax(180px,1fr)",
      cell: (c) =>
        c.description ? (
          <span className="truncate">{c.description}</span>
        ) : (
          editable && <span className="truncate text-faint">e.g. Ocean freight, Shenzhen → LA</span>
        ),
      edit: { get: (c) => c.description, save: (c, v) => saveCharge(c.id, { description: v.trim() }) },
    },
    {
      key: "amount",
      header: "Amount",
      width: "110px",
      align: "right",
      value: (c) => Number(c.amount),
      cell: (c) => <span className="font-mono">{amountText(c.amount)}</span>,
      edit: {
        get: (c) => String(c.amount),
        save: (c, v) => {
          const n = num(v);
          if (n == null) return toast.error("Enter an amount");
          saveCharge(c.id, { amount: n });
        },
      },
    },
    {
      key: "currency",
      header: "Cur.",
      width: "70px",
      cell: (c) => <span className="font-mono uppercase">{c.currency}</span>,
      edit: {
        get: (c) => c.currency,
        save: (c, v) => {
          const currency = v.trim().toUpperCase();
          if (currency === "USD" || !currency) return saveCharge(c.id, { currency: "USD" });
          // A new currency needs its own rate: ask for it now rather than keep the old one.
          const typed = prompt(`USD per 1 ${currency} (from the invoice, or the day's rate):`, "");
          const rate = typed == null ? null : num(typed);
          if (rate == null || rate <= 0) return toast.error(`Enter the USD rate for ${currency} to change the currency`);
          saveCharge(c.id, { currency, usd_rate: rate });
        },
      },
    },
    {
      key: "usd_rate",
      header: "USD rate",
      headerTitle: "USD per unit of the currency",
      width: "96px",
      align: "right",
      value: (c) => Number(c.usd_rate),
      cell: (c) => <span className={cx("font-mono", c.currency === "USD" && "text-faint")}>{String(c.usd_rate)}</span>,
      edit: {
        can: (c) => c.currency !== "USD",
        get: (c) => String(c.usd_rate),
        save: (c, v) => {
          const n = num(v);
          if (n == null || n <= 0) return toast.error("The rate to USD is more than 0");
          saveCharge(c.id, { usd_rate: n });
        },
      },
    },
    {
      key: "method",
      header: "Split",
      width: "140px",
      value: (c) => ALLOCATION_LABELS[c.method],
      cell: (c) => {
        const flag = flagOf(c.id);
        return (
          <>
            <span className="truncate">{ALLOCATION_LABELS[c.method]}</span>
            {flag && (
              <span title={flag} className="shrink-0 text-warn">
                <AlertTriangle className="size-3.5" />
              </span>
            )}
          </>
        );
      },
      edit: {
        options: ALLOCATION_METHODS.map((m) => ({ value: m, label: ALLOCATION_LABELS[m] })),
        get: (c) => c.method,
        save: (c, v) => saveCharge(c.id, { method: v as AllocationMethod }),
      },
    },
    {
      key: "usd",
      header: "USD",
      width: "100px",
      align: "right",
      value: (c) => chargeUsd(c),
      cell: (c) => <span className="font-mono">{usd(chargeUsd(c))}</span>,
      total: () => <span className="font-mono font-semibold">{usd(a.charges_usd)}</span>,
    },
    // The order they were added in (the table's own order); hidden unless someone wants it
    { key: "position", header: "#", width: "48px", align: "right", optional: true, cell: (c) => <span className="text-faint">{c.position + 1}</span> },
    {
      key: "remove",
      header: "",
      width: "40px",
      value: () => "",
      cell: (c) =>
        editable && (
          <button
            type="button"
            className="rounded p-1 text-faint hover:bg-hover hover:text-danger"
            title="Remove"
            onClick={(e) => {
              e.stopPropagation();
              mutate("landed_cost_charge.remove", { id: c.id });
            }}
          >
            <Trash2 className="size-3.5" />
          </button>
        ),
    },
  ];

  const lineRows: LineRow[] = a.lines.map((l) => ({ ...l, id: l.key }));
  const lineColumns: Column<LineRow>[] = [
    ...(multiPo ? [{ key: "po_number", header: "PO", width: "90px", cell: (l: LineRow) => <span className="font-mono">{l.po_number}</span> }] : []),
    { key: "sku", header: "SKU", width: "minmax(110px,1fr)", cell: (l) => <span className="truncate font-mono">{l.sku || "—"}</span> },
    { key: "name", header: "Name", width: "minmax(140px,1.5fr)", cell: (l) => <span className="truncate text-muted">{l.name}</span> },
    { key: "qty", header: "Qty", width: "72px", align: "right", cell: (l) => <span className="font-mono">{qty(l.qty)}</span> },
    {
      key: "unit_price",
      header: "Unit price",
      width: "100px",
      align: "right",
      value: (l) => l.unit_price ?? "",
      cell: (l) => <span className="font-mono">{l.unit_price == null ? "—" : money(l.unit_price, l.currency)}</span>,
    },
    {
      key: "weight_g",
      header: "Weight",
      width: "84px",
      align: "right",
      value: (l) => l.weight_g ?? "",
      cell: (l) => <span className="font-mono text-muted">{l.weight_g == null ? "—" : `${qty(l.weight_g)} g`}</span>,
    },
    {
      key: "goods_usd",
      header: "Goods",
      width: "100px",
      align: "right",
      value: (l) => l.goods_usd ?? "",
      cell: (l) => <span className="font-mono">{usd(l.goods_usd)}</span>,
      total: () => <span className="font-mono">{usd(a.goods_usd)}</span>,
    },
    // One column per charge: its share of each line
    ...a.charges.map(
      (c): Column<LineRow> => ({
        key: `charge:${c.id}`,
        header: kindLabel(charges.find((x) => x.id === c.id)?.kind ?? "other"),
        headerTitle: charges.find((x) => x.id === c.id)?.description || undefined,
        width: "96px",
        align: "right",
        value: (l) => l.by_charge[c.id] ?? "",
        cell: (l) => <span className="font-mono">{usd(l.by_charge[c.id])}</span>,
        total: () => <span className="font-mono">{usd(c.usd)}</span>,
      }),
    ),
    { key: "extra_unit_usd", header: "+ per unit", width: "96px", align: "right", cell: (l) => <span className="font-mono">{money(l.extra_unit_usd)}</span> },
    {
      key: "landed_unit_usd",
      header: "Landed unit",
      width: "104px",
      align: "right",
      value: (l) => l.landed_unit_usd ?? "",
      cell: (l) => <span className="font-mono font-semibold">{usd(l.landed_unit_usd)}</span>,
      total: () => <span className="font-mono font-semibold">{usd(a.landed_usd)}</span>,
    },
    {
      key: "goes",
      header: "Cost goes to",
      width: "minmax(130px,1fr)",
      value: (l) => (l.ledger ? "Stock ledger" : l.shopify ? "Shopify (optional)" : "—"),
      cell: (l) => <span className="truncate text-muted">{l.ledger ? "Stock ledger" : l.shopify ? "Shopify (optional)" : "—"}</span>,
    },
  ];

  return (
    <div className="flex flex-col gap-4 p-4 text-[13px]">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono font-semibold">{lc.number}</span>
        <Badge tone={STATUS_TONE[lc.status]}>{lc.status === "applied" ? `Applied ${ago(lc.applied_at)}` : lc.status === "void" ? "Void" : "Draft"}</Badge>
        {Math.abs(onLedger) >= 0.005 && <Badge tone="accent">{money(onLedger)} on the stock ledger</Badge>}
        <div className="ml-auto flex items-center gap-2">
          {draft && can("landed_cost.apply") && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => act("landed_cost.apply")}
              disabled={estimate || !charges.length}
              title={estimate ? "Nothing has been received yet" : "Puts local items' shares on their receipt moves"}
            >
              Apply to stock ledger
            </Button>
          )}
          {lc.status !== "void" && shopifyLines.length > 0 && can("landed_cost.update_shopify_costs") && (
            <Button size="sm" icon={<Store className="size-3.5" />} onClick={() => setPreviewing(true)} disabled={estimate || !charges.length}>
              Update Shopify costs…
            </Button>
          )}
          {lc.status === "applied" && can("landed_cost.reopen") && (
            <Button size="sm" onClick={() => act("landed_cost.reopen")} title="Change it again; the ledger keeps what was applied until you apply again">
              Reopen
            </Button>
          )}
          {lc.status !== "void" && Math.abs(onLedger) >= 0.005 && can("landed_cost.void") && (
            <Button size="sm" variant="danger" onClick={() => act("landed_cost.void", `Void ${lc.number}? What it put on the stock ledger comes back off.`)}>
              Void
            </Button>
          )}
          {draft && Math.abs(onLedger) < 0.005 && !lc.applied_at && can("landed_cost.remove") && (
            <button type="button" className="rounded p-1 text-faint hover:bg-hover hover:text-danger" title="Delete this draft" onClick={remove}>
              <Trash2 className="size-4" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-muted">Forwarder</span>
          <SaveInput value={lc.forwarder} disabled={!editable} placeholder="e.g. Flexport" onSave={(v) => update({ forwarder: v.trim() })} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-muted">Their invoice</span>
          <SaveInput value={lc.invoice_ref} disabled={!editable} placeholder="Invoice number" onSave={(v) => update({ invoice_ref: v.trim() })} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-muted">Invoice date</span>
          <SaveInput type="date" value={lc.invoice_date ?? ""} disabled={!editable} onSave={(v) => update({ invoice_date: v || null })} />
        </label>
      </div>
      {/* Its documents: the forwarder's invoice (and anything else), dropped, pasted or chosen */}
      <Attachments owner={{ type: "landed_cost", id: lc.id }} kind="invoice" hint="Drop the forwarder's invoice (PDF) here, paste it, or choose it" />

      {/* What it covers */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-3 text-[12px]">
          <span className="text-muted">Covers</span>
          {lc.po_id && (
            <label className="inline-flex items-center gap-1">
              <input type="radio" checked={lc.scope === "po"} disabled={!editable} onChange={() => setScope("po")} /> Everything received on{" "}
              {pos.find((p) => p.id === lc.po_id)?.number ?? "its PO"}
            </label>
          )}
          <label className="inline-flex items-center gap-1">
            <input type="radio" checked={lc.scope === "receipts"} disabled={!editable} onChange={() => setScope("receipts")} /> These receipts (one delivery, or
            POs shipped together)
          </label>
        </div>
        {lc.scope === "receipts" && (
          <div className="flex flex-wrap items-center gap-1.5">
            {pickable.map((r) => (
              <label
                key={r.id}
                className={cx(
                  "inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[12px]",
                  chosen.some((l) => l.receipt_id === r.id) ? "border-accent" : "border-line",
                )}
              >
                <input type="checkbox" checked={chosen.some((l) => l.receipt_id === r.id)} disabled={!editable} onChange={() => toggleReceipt(r.id)} />
                {r.number}
              </label>
            ))}
            {editable && others.length > 0 && (
              <Select
                value=""
                aria-label="Add a receipt from another PO"
                options={[
                  { value: "", label: "Add a receipt from another PO…" },
                  ...others.map((r) => ({ value: r.id, label: `${r.number} · ${ago(r.received_at)}` })),
                ]}
                onChange={(e) => e.target.value && toggleReceipt(e.target.value)}
                className="h-7 w-64 text-[12px]"
              />
            )}
            {!pickable.length && <span className="text-muted">Nothing received yet.</span>}
          </div>
        )}
        {estimate && (
          <div className="text-[12px] text-warn">Nothing has been received yet, so this splits over the lines as ordered. Apply it once the goods arrive.</div>
        )}
      </div>

      {/* Goods rates */}
      {currencies.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 text-[12px]">
          <span className="text-muted">Goods in USD:</span>
          {currencies.map((cur) => (
            <label key={cur} className="inline-flex items-center gap-1.5">
              1 {cur} =
              <SaveInput
                value={rates[cur] == null ? "" : String(rates[cur])}
                disabled={!editable}
                className="w-24 text-right font-mono"
                placeholder="rate"
                aria-label={`USD per ${cur}`}
                onSave={(v) => {
                  const n = num(v);
                  const next = { ...rates };
                  if (n == null) delete next[cur];
                  else next[cur] = n;
                  update({ goods_rates: next });
                }}
              />
              USD
            </label>
          ))}
        </div>
      )}

      {/* Charges: a spreadsheet while it's a draft (click a cell to type, Enter saves and moves down) */}
      <div>
        <div className="mb-1 flex items-center gap-3">
          <span className="text-[11px] font-medium tracking-wide text-faint uppercase">Charges</span>
          {editable && (
            <button type="button" onClick={addCharge} className="inline-flex items-center gap-1 text-[12px] text-accent-text hover:underline">
              <Plus className="size-3.5" /> Add a charge
            </button>
          )}
        </div>
        <DataTable
          fit
          rows={charges}
          columns={chargeColumns}
          editing={editable}
          defaultSort={{ key: "position" }}
          exportName="Landed cost charges"
          pageFilters={false}
          rowNoun={["charge", "charges"]}
          empty={<div className="p-4 text-center text-[12px] text-muted">No charges yet.</div>}
        />
      </div>

      {a.flags.map((f) => (
        <div key={f} className="flex items-start gap-1.5 text-[12px] text-warn">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          {f}
        </div>
      ))}

      {/* Lines */}
      <div>
        <div className="mb-1 flex items-center gap-2">
          <span className="text-[11px] font-medium tracking-wide text-faint uppercase">Landed cost per line (USD)</span>
        </div>
        <DataTable
          fit={20}
          rows={lineRows}
          columns={lineColumns}
          exportName="Landed cost per line"
          pageFilters={false}
          rowNoun={["line", "lines"]}
          empty={<div className="p-4 text-center text-[12px] text-muted">Nothing to split over yet.</div>}
        />
      </div>
      {previewing && <ShopifyCostDialog lc={lc} alloc={a} charges={charges} onClose={() => setPreviewing(false)} />}
    </div>
  );
}

/** The weighted-average unit cost each Shopify item would get; nothing changes until Update. */
function ShopifyCostDialog({
  lc,
  alloc,
  charges,
  onClose,
}: {
  lc: LandedCost;
  alloc: ReturnType<typeof allocate>;
  charges: { currency: string; usd_rate: number }[];
  onClose: () => void;
}) {
  const items = useRows("item");
  const levels = useRows("stock_level");
  const locations = useRows("location");
  const rows = useMemo(() => {
    const hhl = new Set(locations.filter((l) => l.kind === "hhl").map((l) => l.id));
    const onHand = new Map<string, number>();
    for (const s of levels) if (hhl.has(s.location_id)) onHand.set(s.item_id, (onHand.get(s.item_id) ?? 0) + Number(s.on_hand));
    // Items this landed cost already updated start again from the cost before it, so a re-run never compounds.
    return shopifyCostPreview(alloc, items, onHand, (Array.isArray(lc.shopify_costs) ? lc.shopify_costs : []) as AppliedCost[]);
  }, [alloc, items, levels, locations, lc.shopify_costs]);
  // Already-updated items aren't ticked: tick one to apply a change (a late charge) on purpose.
  const [picked, setPicked] = useState(() => new Set(rows.filter((r) => !r.applied && r.next !== r.current).map((r) => r.item_id)));
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    const costs = rows.filter((r) => picked.has(r.item_id)).map((r) => ({ item_id: r.item_id, std_cost: r.next }));
    if (!costs.length) return onClose();
    setBusy(true);
    const confirm_rates = confirmRates(charges);
    if (confirm_rates === false) return;
    const { ok } = await mutateWithResult("landed_cost.update_shopify_costs", { id: lc.id, costs, confirm_rates });
    setBusy(false);
    if (ok) {
      toast.ok(`${costs.length} unit cost${costs.length === 1 ? "" : "s"} going to Shopify`);
      onClose();
    }
  };
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="w-[760px] rounded-xl border border-line bg-surface p-4 shadow-pop"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <div className="text-[15px] font-semibold">Update Shopify unit cost</div>
        <p className="mt-1 text-[12px] text-muted">
          The stock already on hand at its current cost, plus what {lc.number} brought in at its landed cost, averaged. On hand is Shopify's now at HHL, so
          units sold since the delivery count as older stock. Rounded to the cent; it goes to Shopify like a catalog edit.
        </p>
        <table className="mt-3 w-full text-[12px]">
          <thead className="text-left text-faint">
            <tr>
              <th className="w-6" />
              <th className="py-1 font-normal">SKU</th>
              <th className="py-1 text-right font-normal">On hand</th>
              <th className="py-1 text-right font-normal">Arrived</th>
              <th className="py-1 text-right font-normal">Now</th>
              <th className="py-1 text-right font-normal">Landed</th>
              <th className="py-1 text-right font-normal">New cost</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.item_id} className="border-t border-line">
                <td className="py-1">
                  <input
                    type="checkbox"
                    checked={picked.has(r.item_id)}
                    onChange={() =>
                      setPicked((prev) => {
                        const next = new Set(prev);
                        if (next.has(r.item_id)) next.delete(r.item_id);
                        else next.add(r.item_id);
                        return next;
                      })
                    }
                  />
                </td>
                <td className="py-1">
                  <span className="font-mono">{r.sku}</span> <span className="text-muted">{r.name}</span>
                  {r.note && <div className="text-[11px] text-faint">{r.note}</div>}
                </td>
                <td className="py-1 text-right font-mono">{qty(r.on_hand)}</td>
                <td className="py-1 text-right font-mono">{qty(r.received)}</td>
                <td className="py-1 text-right font-mono">{r.current == null ? "—" : money(r.current)}</td>
                <td className="py-1 text-right font-mono">{money(r.landed_unit)}</td>
                <td className={cx("py-1 text-right font-mono font-semibold", r.next === r.current && "text-faint")}>{money(r.next)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={apply} disabled={busy || !picked.size}>
            Update {picked.size} in Shopify
          </Button>
        </div>
      </div>
    </DialogLayer>
  );
}
